import { useCallback, useRef, useState } from 'react'
import { useMutation } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { Id } from '../../../../convex/_generated/dataModel'
import type { ClipData } from './buildDocument'
import type { Editor, JSONContent } from '@tiptap/react'

interface TimedWord {
  start: number
  end: number
  text: string
}

export interface PendingEdit {
  clipId: string
  removeStart: number
  removeEnd: number
  type: 'trim_start' | 'trim_end' | 'split'
  generationMode: string
  removedText: string
}

function extractTimedWordsFromDoc(doc: JSONContent): Map<string, TimedWord[]> {
  const result = new Map<string, TimedWord[]>()
  if (!doc.content) return result

  let currentClipId = ''
  let currentClipStartTime = 0

  for (const node of doc.content) {
    if (node.type === 'sceneBlock') {
      currentClipId = node.attrs?.clipId || ''
      currentClipStartTime = node.attrs?.clipStartTime || 0
    }
    if (node.type === 'dialogBlock' && node.attrs?.sourceType === 'transcription') {
      const clipId = node.attrs?.clipId || currentClipId
      const words: TimedWord[] = []
      if (node.content) {
        for (const child of node.content) {
          if (child.marks) {
            for (const mark of child.marks) {
              if (mark.type === 'wordMark' && mark.attrs) {
                words.push({
                  start: mark.attrs.start - currentClipStartTime,
                  end: mark.attrs.end - currentClipStartTime,
                  text: (child.text || '').trim(),
                })
              }
            }
          }
        }
      }
      const existing = result.get(clipId) || []
      result.set(clipId, [...existing, ...words])
    }
  }

  return result
}

export function useScriptEditPropagation(
  clips: ClipData[],
  workflowId?: Id<'workflows'>,
) {
  const [pendingEdit, setPendingEdit] = useState<PendingEdit | null>(null)
  const originalWordsRef = useRef<Map<string, TimedWord[]>>(new Map())
  const initializedRef = useRef(false)
  const trimFromScript = useMutation(api.workflows.trimFromScript)

  const initializeFromDoc = useCallback((doc: JSONContent) => {
    if (initializedRef.current) return
    originalWordsRef.current = extractTimedWordsFromDoc(doc)
    initializedRef.current = true
  }, [])

  const checkForDeletions = useCallback((editor: Editor) => {
    const doc = editor.getJSON()
    const currentWords = extractTimedWordsFromDoc(doc)

    for (const [clipId, origWords] of originalWordsRef.current) {
      if (origWords.length === 0) continue
      const currWords = currentWords.get(clipId) || []

      const currSet = new Set(currWords.map(w => `${w.start.toFixed(2)}:${w.end.toFixed(2)}`))
      const removed = origWords.filter(w => !currSet.has(`${w.start.toFixed(2)}:${w.end.toFixed(2)}`))

      if (removed.length === 0) continue

      const removeStart = Math.min(...removed.map(w => w.start))
      const removeEnd = Math.max(...removed.map(w => w.end))
      const removedText = removed.map(w => w.text).join(' ')

      const clip = clips.find(c => c._id === clipId)
      if (!clip) continue

      const visibleStart = clip.trimStart ?? 0
      const visibleEnd = clip.duration - (clip.trimEnd ?? 0)
      const epsilon = 0.15

      let type: PendingEdit['type']
      if (removeStart <= visibleStart + epsilon) {
        type = 'trim_start'
      } else if (removeEnd >= visibleEnd - epsilon) {
        type = 'trim_end'
      } else {
        type = 'split'
      }

      setPendingEdit({
        clipId,
        removeStart,
        removeEnd,
        type,
        generationMode: clip.generationMode || '',
        removedText,
      })
      return
    }
  }, [clips])

  const confirmTrim = useCallback(async () => {
    if (!pendingEdit || !workflowId) return
    try {
      await trimFromScript({
        clipId: pendingEdit.clipId as Id<'workflowClips'>,
        removeStartSec: pendingEdit.removeStart,
        removeEndSec: pendingEdit.removeEnd,
      })
    } catch (e) {
      console.error('trimFromScript failed:', e)
    }
    originalWordsRef.current.delete(pendingEdit.clipId)
    initializedRef.current = false
    setPendingEdit(null)
  }, [pendingEdit, workflowId, trimFromScript])

  const dismiss = useCallback(() => {
    setPendingEdit(null)
  }, [])

  const resetOriginals = useCallback(() => {
    initializedRef.current = false
    originalWordsRef.current = new Map()
  }, [])

  return {
    pendingEdit,
    initializeFromDoc,
    checkForDeletions,
    confirmTrim,
    dismiss,
    resetOriginals,
  }
}
